import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

export interface TopicSuggestion {
  id: string;
  type: 'add' | 'edit' | 'delete';
  title: string;
  description?: string;
  parentTopicId?: string | number;
  parentTopicTitle?: string;
  prerequisites?: Array<{ id: string | number; title: string }>;
  displayOrder?: number;
  originalData?: any; // For edits/deletes
  changes?: {
    title?: { old: string; new: string };
    description?: { old: string; new: string };
    parentTopicId?: { old?: number; new?: number };
  };
  reason?: string; // For deletions
}

interface ReviewChangesDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (acceptedSuggestions: TopicSuggestion[]) => void;
  suggestions: TopicSuggestion[];
  existingTopics: any[];
  applying?: boolean;
}

const ReviewChangesDialog: React.FC<ReviewChangesDialogProps> = ({
  open,
  onClose,
  onApply,
  suggestions,
  existingTopics,
  applying = false,
}) => {
  const [acceptedIds, setAcceptedIds] = useState<Set<string>>(new Set());
  const [editedSuggestions, setEditedSuggestions] = useState<Map<string, TopicSuggestion>>(new Map());

  const addSuggestions = suggestions.filter(s => s.type === 'add');
  const editSuggestions = suggestions.filter(s => s.type === 'edit');
  const deleteSuggestions = suggestions.filter(s => s.type === 'delete');

  const toggleAccept = (id: string) => {
    const newAccepted = new Set(acceptedIds);
    if (newAccepted.has(id)) {
      newAccepted.delete(id);
    } else {
      newAccepted.add(id);
    }
    setAcceptedIds(newAccepted);
  };

  const handleAcceptAll = () => {
    setAcceptedIds(new Set(suggestions.map(s => s.id)));
  };

  const handleRejectAll = () => {
    setAcceptedIds(new Set());
  };

  const handleApply = () => {
    const accepted = suggestions.filter(s => acceptedIds.has(s.id));
    const edited = Array.from(editedSuggestions.values()).filter(s => acceptedIds.has(s.id));
    // Replace edited suggestions
    const final = accepted.map(s => editedSuggestions.get(s.id) || s);
    onApply(final);
  };

  const renderTopicTree = (suggestions: TopicSuggestion[], parentSuggestionId: string | null = null, level: number = 0): React.ReactNode => {
    // For root topics, find those without parentTopicId or with parentTopicId matching existing topics
    const filtered = suggestions.filter(s => {
      if (parentSuggestionId === null) {
        // Root level: no parentTopicId or parentTopicId points to existing topic (not a new suggestion)
        // If parentTopicId exists but doesn't match any suggestion ID, it's a root relative to the suggestions (depends on existing topic)
        return !s.parentTopicId || !suggestions.find(p => p.id === s.parentTopicId?.toString());
      }
      // Find parent suggestion by matching parentTopicId
      const parent = suggestions.find(p => p.id === parentSuggestionId);
      if (!parent) return false;

      // Match by parentTopicId
      // Ensure we compare strings to strings
      return s.parentTopicId?.toString() === parentSuggestionId;
    });

    return filtered.map((suggestion) => {
      const isAccepted = acceptedIds.has(suggestion.id);
      const edited = editedSuggestions.get(suggestion.id);
      const finalSuggestion = edited || suggestion;
      const children = renderTopicTree(suggestions, suggestion.id, level + 1);

      return (
        <Box key={suggestion.id} sx={{ pl: level * 3, mb: 1 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderLeft: `4px solid ${suggestion.type === 'add' ? 'success.main' :
                suggestion.type === 'edit' ? 'warning.main' :
                  'error.main'
                }`,
              bgcolor: isAccepted
                ? suggestion.type === 'add' ? 'success.light' :
                  suggestion.type === 'edit' ? 'warning.light' :
                    'error.light'
                : 'background.paper',
              opacity: isAccepted ? 1 : 0.7,
            }}
          >
            <Box display="flex" alignItems="start" gap={1}>
              <Checkbox
                checked={isAccepted}
                onChange={() => toggleAccept(suggestion.id)}
                size="small"
                sx={{ mt: -0.5 }}
              />

              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  {suggestion.type === 'add' && <AddIcon fontSize="small" color="success" />}
                  {suggestion.type === 'edit' && <EditIcon fontSize="small" color="warning" />}
                  {suggestion.type === 'delete' && <DeleteIcon fontSize="small" color="error" />}

                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{
                      textDecoration: suggestion.type === 'edit' && finalSuggestion.changes?.title
                        ? 'line-through'
                        : 'none',
                      color: suggestion.type === 'delete' ? 'error.main' : 'text.primary',
                    }}
                  >
                    {suggestion.type === 'edit' && finalSuggestion.changes?.title
                      ? finalSuggestion.changes.title.old
                      : finalSuggestion.title}
                  </Typography>

                  {suggestion.type === 'edit' && finalSuggestion.changes?.title && (
                    <Typography variant="body2" color="success.main" fontWeight="bold">
                      → {finalSuggestion.changes.title.new}
                    </Typography>
                  )}

                  <Chip
                    label={suggestion.type.toUpperCase()}
                    size="small"
                    color={
                      suggestion.type === 'add' ? 'success' :
                        suggestion.type === 'edit' ? 'warning' :
                          'error'
                    }
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                </Box>

                {finalSuggestion.description && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    {finalSuggestion.description}
                  </Typography>
                )}

                {suggestion.type === 'edit' && finalSuggestion.changes?.description && (
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                      {finalSuggestion.changes.description.old}
                    </Typography>
                    <Typography variant="caption" color="success.main" display="block">
                      → {finalSuggestion.changes.description.new}
                    </Typography>
                  </Box>
                )}

                {finalSuggestion.parentTopicTitle && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Parent: {finalSuggestion.parentTopicTitle}
                  </Typography>
                )}

                {finalSuggestion.prerequisites && finalSuggestion.prerequisites.length > 0 && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Prerequisites:{' '}
                      {finalSuggestion.prerequisites.map(p => p.title).join(', ')}
                    </Typography>
                  </Box>
                )}

                {suggestion.type === 'delete' && suggestion.reason && (
                  <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
                    Reason: {suggestion.reason}
                  </Typography>
                )}
              </Box>

              <IconButton
                size="small"
                onClick={() => {
                  // TODO: Open edit dialog
                  console.log('Edit suggestion:', suggestion.id);
                }}
                title="Edit suggestion"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
          {children}
        </Box>
      );
    });
  };

  const acceptedCount = acceptedIds.size;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Review AI Suggestions</Typography>
          <Box display="flex" gap={1}>
            <Button size="small" onClick={handleAcceptAll}>
              Accept All
            </Button>
            <Button size="small" onClick={handleRejectAll}>
              Reject All
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            AI has suggested {suggestions.length} changes. Review and select which ones to apply.
          </Typography>
        </Box>

        {/* New Topics */}
        {addSuggestions.length > 0 && (
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <AddIcon color="success" fontSize="small" />
                <Typography variant="subtitle1">
                  New Topics ({addSuggestions.length})
                </Typography>
                <Chip
                  label={`${addSuggestions.filter(s => acceptedIds.has(s.id)).length} accepted`}
                  size="small"
                  color="success"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {renderTopicTree(addSuggestions)}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Modified Topics */}
        {editSuggestions.length > 0 && (
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <EditIcon color="warning" fontSize="small" />
                <Typography variant="subtitle1">
                  Modified Topics ({editSuggestions.length})
                </Typography>
                <Chip
                  label={`${editSuggestions.filter(s => acceptedIds.has(s.id)).length} accepted`}
                  size="small"
                  color="warning"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {editSuggestions.map((suggestion) => {
                  const isAccepted = acceptedIds.has(suggestion.id);
                  const edited = editedSuggestions.get(suggestion.id);
                  const finalSuggestion = edited || suggestion;

                  return (
                    <Paper
                      key={suggestion.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        mb: 1,
                        borderLeft: '4px solid',
                        borderLeftColor: 'warning.main',
                        bgcolor: isAccepted ? 'warning.light' : 'background.paper',
                        opacity: isAccepted ? 1 : 0.7,
                      }}
                    >
                      <Box display="flex" alignItems="start" gap={1}>
                        <Checkbox
                          checked={isAccepted}
                          onChange={() => toggleAccept(suggestion.id)}
                          size="small"
                        />
                        <Box flex={1}>
                          <Typography variant="body2" fontWeight="bold">
                            {finalSuggestion.originalData?.title || 'Unknown Topic'}
                          </Typography>
                          {finalSuggestion.changes && (
                            <Box sx={{ mt: 1 }}>
                              {finalSuggestion.changes.title && (
                                <Box>
                                  <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                    Title: {finalSuggestion.changes.title.old}
                                  </Typography>
                                  <Typography variant="caption" color="success.main" display="block">
                                    → {finalSuggestion.changes.title.new}
                                  </Typography>
                                </Box>
                              )}
                              {finalSuggestion.changes.description && (
                                <Box sx={{ mt: 0.5 }}>
                                  <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                    Description: {finalSuggestion.changes.description.old}
                                  </Typography>
                                  <Typography variant="caption" color="success.main" display="block">
                                    → {finalSuggestion.changes.description.new}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => console.log('Edit:', suggestion.id)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Deleted Topics */}
        {deleteSuggestions.length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <DeleteIcon color="error" fontSize="small" />
                <Typography variant="subtitle1">
                  Deleted Topics ({deleteSuggestions.length})
                </Typography>
                <Chip
                  label={`${deleteSuggestions.filter(s => acceptedIds.has(s.id)).length} accepted`}
                  size="small"
                  color="error"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {deleteSuggestions.map((suggestion) => {
                  const isAccepted = acceptedIds.has(suggestion.id);

                  return (
                    <Paper
                      key={suggestion.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        mb: 1,
                        borderLeft: '4px solid',
                        borderLeftColor: 'error.main',
                        bgcolor: isAccepted ? 'error.light' : 'background.paper',
                        opacity: isAccepted ? 1 : 0.7,
                      }}
                    >
                      <Box display="flex" alignItems="start" gap={1}>
                        <Checkbox
                          checked={isAccepted}
                          onChange={() => toggleAccept(suggestion.id)}
                          size="small"
                        />
                        <Box flex={1}>
                          <Typography variant="body2" fontWeight="bold" color="error.main">
                            {suggestion.title}
                          </Typography>
                          {suggestion.reason && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              Reason: {suggestion.reason}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {suggestions.length === 0 && (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              No suggestions to review.
            </Typography>
          </Box>
        )}
      </DialogContent>
      {applying && <LinearProgress />}
      <DialogActions>
        <Button onClick={onClose} disabled={applying}>Cancel</Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={acceptedCount === 0 || applying}
          startIcon={applying ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
        >
          {applying ? 'Applying changes...' : `Apply Selected Changes (${acceptedCount})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewChangesDialog;

